import {Behavior} from './ToggleArea';
import FocusJail from './FocusJail';
import mergeOpts from './merge-opts';
import {delegate} from './events';
import {fd, mutate} from './promise-fastdom';
import focusElement from './focus-element';
import promiseRAF from './promise-raf';
import focusables from "./focusables";

const _ = new WeakMap();

/*
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/dialog-modal/
 */
export default function ModalBehavior(openButton, closeButton, o) {

    o = mergeOpts({
        closeOnEsc: true,
        closeOnOutsideClick: false,
        focusOnArea: false,
        outside: document.querySelectorAll('.wrapper'),
    }, o);

    o.openButton = openButton;
    o.closeButton = closeButton;
    this.active = false;

    _.set(this, o);
}

ModalBehavior.prototype = new Behavior();

ModalBehavior.prototype.toggle = function () {
    const o = _.get(this);
    o.update(!this.active);
};

ModalBehavior.prototype.init = function (instance) {

    const o = _.get(this);

    o.jail = new FocusJail([instance.area()]);

    o.update = (active) => {
        this.active = active;
        o.openButton.setAttribute('aria-expanded', this.active);
        instance.update();

        return this.active
            ? Promise.resolve()
                .then(promiseRAF())
                .then(mutate(() => {
                    o.jail.jail();

                    // focus
                    let target = instance.area();
                    if (!o.focusOnArea) {
                        for (const candidate of focusables(instance.area())) {
                            if (candidate !== o.closeButton) {
                                target = candidate;
                                break;
                            }
                        }
                    }
                    focusElement.call(target, true);

                    o.outside.forEach(node => {
                        node.inert = true;
                        node.setAttribute('aria-hidden', 'true');
                    });
                }))
            : Promise.resolve()
                .then(mutate(() => {
                    o.outside.forEach(node => {
                        node.inert = false;
                        node.removeAttribute('aria-hidden');
                    });
                    o.jail.free();
                    focusElement.call(o.openButton, true);
                }))
            ;
    };

    o.toggleHandler = e => {
        e.preventDefault();
        o.update(!this.active);
    };

    o.keydownToggleHandler = e => {
        if (e.code === 'Space' || e.code === 'Enter') {
            e.preventDefault();
            o.toggleHandler(e);
        }
    };

    if (o.closeOnLinkClick) {
        o.closeOnLinkClickHandler = delegate('a[href]', () => {
            o.update(false);
        });
        instance.area().addEventListener('click', o.closeOnLinkClickHandler);
    }

    if (o.closeOnOutsideClick) {
        o.outsideClickHandler = (e) => {
            if (!this.active) {
                return;
            }
            if (o.openButton.contains(e.target)) {
                return;
            }
            if (instance.area().contains(e.target)) {
                return;
            }
            o.update(false);
        };
        document.addEventListener('click', o.outsideClickHandler, {passive: true});
    }

    if (o.closeOnEsc) {
        o.escapeHandler = (e) => {
            // press on ESC
            if (!this.active) {
                return;
            }
            if (e.code !== 'Escape') {
                return;
            }
            if (!instance.area().contains(document.activeElement)) {
                return;
            }
            o.update(false);
        };
        document.addEventListener('keydown', o.escapeHandler, {passive: true});
    }

    if (instance.area().id) {
        o.delegateToggleHandler = delegate(`a[href="#${instance.area().id}"]`, e => {
            if (o.openButton.contains(e.target)) {
                return;
            }
            focusElement.call(o.openButton, true);
            o.toggleHandler(e);
        });
        document.addEventListener('click', o.delegateToggleHandler);
    }

    return fd.mutate(() => {

        const area = instance.area();
        area.setAttribute('role', 'dialog');
        area.setAttribute('aria-dialog', 'true');

        o.openButton.disabled = false;
        o.openButton.addEventListener('click', o.toggleHandler);
        o.openButton.setAttribute('aria-controls', instance.area().id);
        o.openButton.setAttribute('aria-haspopup', 'true');
        o.openButton.setAttribute('aria-expanded', this.active);

        if (o.openButton.tagName === 'A') {
            o.openButton.dataset['href'] = o.openButton.href;
            o.openButton.removeAttribute('href');
        }

        if (o.openButton.tagName !== 'BUTTON') {
            o.openButton.setAttribute('role', 'button');

            // needed otherwise button can not be tabbed
            o.openButton.setAttribute('tabindex', 0);
            o.openButton.tabIndex = 0;
            o.openButton.addEventListener('keydown', o.keydownToggleHandler);
        }

        o.closeButton.disabled = false;
        o.closeButton.addEventListener('click', o.toggleHandler);
    });
};

ModalBehavior.prototype.jail = function () {
    const o = _.get(this);
    return o.jail;
};

ModalBehavior.prototype.toggleClass = function (className, toggle) {
    const o = _.get(this);
    o.openButton.classList.toggle(className, toggle);
};

ModalBehavior.prototype.destroy = function (instance) {
    const o = _.get(this);

    o.openButton.removeEventListener('click', o.toggleHandler);
    o.closeButton.removeEventListener('click', o.toggleHandler);

    if (o.closeOnOutsideClick) {
        document.removeEventListener('click', o.outsideClickHandler);
    }

    if (o.closeOnEsc) {
        document.removeEventListener('keydown', o.escapeHandler);
    }

    if (instance.area().id) {
        document.removeEventListener('click', o.delegateToggleHandler);
    }

    o.jail.destroy();

    return fd.mutate(() => {
        const area = instance.area();
        area.removeAttribute('role');
        area.removeAttribute('aria-dialog');
        o.openButton.removeAttribute('aria-controls');
        o.openButton.removeAttribute('aria-haspopup');
        o.openButton.removeAttribute('aria-expanded');

        if (o.openButton.tagName === 'A') {
            o.openButton.setAttribute('href', o.openButton.dataset['href']);
            o.openButton.removeAttribute('data-href');
        }

        if (o.openButton.tagName !== 'BUTTON') {
            o.openButton.removeAttribute('role');
            o.openButton.tabIndex = null;
            o.openButton.removeEventListener('keydown', o.keydownToggleHandler);
        }

        if (this.active) {
            o.outside.forEach(node => {
                node.inert = false;
                node.removeAttribute('aria-hidden');
            });
        }

        _.delete(this);
    });
};
